import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation } from 'react-router-dom'

export interface SupportEntitySuggestion {
  type: 'accommodation' | 'channel' | 'provider'
  code: string
  label: string
}

export interface SupportScreenData {
  affectedEntities: SupportEntitySuggestion[]
  relatedPromotions?: SupportPromotionSuggestion[]
  relatedDistribution?: SupportDistributionSuggestion[]
}

export interface SupportWalletTransactionSuggestion {
  id: string
}

export type SupportDistributionSuggestion = {
  propertyCode: string
  label: string
} & (
  | { refType: 'RATE_PLAN'; roomTypeCode: string; ratePlanCode: string }
  | { refType: 'CHANNEL_MAPPING'; channelCode: string }
)

export interface SupportPromotionSuggestion {
  id: string
  label: string
}

const EMPTY: SupportScreenData = { affectedEntities: [] }
type Registration = { owner: object; route: string; data: SupportScreenData }
interface ScreenContext extends SupportScreenData {
  register: (owner: object, data: SupportScreenData) => () => void
}
const Context = createContext<ScreenContext>({
  ...EMPTY,
  register: () => () => {}
})

/** A registration belongs to one location; old cleanup cannot erase a new screen. */
export function SupportScreenProvider({
  children
}: {
  children: React.ReactNode
}) {
  const { pathname } = useLocation()
  const [registration, setRegistration] = useState<Registration>()
  const register = useCallback(
    (owner: object, data: SupportScreenData) => {
      setRegistration({ owner, route: pathname, data })
      return () =>
        setRegistration((current) =>
          current?.owner === owner ? undefined : current
        )
    },
    [pathname]
  )
  const value = useMemo(
    () => ({
      ...(registration?.route === pathname ? registration.data : EMPTY),
      register
    }),
    [pathname, registration, register]
  )
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useSupportScreenContext() {
  return useContext(Context)
}

/** Call with data only once the screen's authoritative query has completed. */
export function useRegisterSupportScreen(data: SupportScreenData) {
  const { register } = useSupportScreenContext()
  const owner = useRef({})
  const encoded = JSON.stringify(data)
  useEffect(
    () => register(owner.current, JSON.parse(encoded)),
    [register, encoded]
  )
}
